import React, { useState, useEffect, useRef } from "react";
import { Table, Input, Space, Popconfirm, Tooltip } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PoweroffOutlined,
  StarFilled,
  StarOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import "./GentricTable.css";
import { debounce } from "lodash";


const GenericTable = ({
  data,
  searchKeys,
  sortableColumns,
  searchInput,
  actions,
  actionFunctions,
  filterableColumns,
  toggle,
  isDefault,
  pagination,
  onSearch,
}) => {
  const [filteredData, setFilteredData] = useState(data);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setFilteredData(data);
    if (searchInput) {
      setSearchTerm(searchInput);
    }
  }, [data]);

  // Utility function to get nested property value
  const getNestedValue = (obj, path) => {
    if (!path) return undefined;
    return path.split(/[.[\]]+/).filter(Boolean).reduce((acc, key) => acc?.[key], obj);
  };

  const debouncedSearch = useRef(
    debounce((value) => {
      if (onSearch) onSearch(value.trim().toLowerCase());
    }, 500)
  ).current;

  const handleSearch = (value) => {
    setSearchTerm(value);
    debouncedSearch(value);
  };



  // Generate unique values for filterable columns
  const getUniqueValues = (key) => {
    const uniqueValues = [
      ...new Set(data.map((item) => getNestedValue(item, key))),
    ];
    return uniqueValues.map((value) => ({
      text: value?.toString(),
      value: value?.toString(),
    }));
  };

  // Handle Filter Functionality
  const handleFilter = (value, record, key) => {
    return getNestedValue(record, key)?.toString() === value;
  };

  // Generate columns dynamically
  const columns = sortableColumns.map((key) => ({
    title: key
      .split(/[[\].]/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" "),
    dataIndex: key,
    key,
    sorter: (a, b) => {
      const valA = getNestedValue(a, key);
      const valB = getNestedValue(b, key);
      if (typeof valA === "string") return valA.localeCompare(valB);
      return (valA || 0) - (valB || 0);
    },
    filters: filterableColumns?.includes(key) ? getUniqueValues(key) : null,
    onFilter: filterableColumns?.includes(key)
      ? (value, record) => handleFilter(value, record, key)
      : null,
  }));

  // Add actions column if actions are provided
  if (actions && actions.length > 0) {
    columns.push({
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <Space size="middle">
          {actions.map((action, index) => {
            const handleClick = () => actionFunctions[index](record);
            switch (action.toLowerCase()) {
              case "delete":
                return (
                  <Tooltip title="Delete" key={action}>
                    <Popconfirm
                      title="Are you sure to delete this item?"
                      onConfirm={handleClick}
                      okText="Yes"
                      cancelText="No"
                    >
                      <DeleteOutlined style={{ color: "red", cursor: "pointer" }} />
                    </Popconfirm>
                  </Tooltip>
                );
              case "edit":
                return (
                  <Tooltip title="Edit" key={action}>
                    <EditOutlined
                      style={{ cursor: "pointer" }}
                      onClick={handleClick}
                    />
                  </Tooltip>
                );
              case "view":
                return (
                  <Tooltip title="View" key={action}>
                    <EyeOutlined
                      style={{ cursor: "pointer" }}
                      onClick={handleClick}
                    />
                  </Tooltip>
                );
              case "toggle status":
                return (
                  <Tooltip
                    title={record[toggle] === "ACTIVE" ? "Deactivate" : "Activate"}
                    key={action}
                  >
                    <Popconfirm
                      title="Are you sure to change the status?"
                      onConfirm={handleClick}
                      okText="Yes"
                      cancelText="No"
                    >
                      <PoweroffOutlined
                        style={{
                          cursor: "pointer",
                          color: record[toggle] === "ACTIVE" ? "green" : "gray",
                        }}
                      />
                    </Popconfirm>
                  </Tooltip>
                );
              default:
                return (
                  <span
                    key={action}
                    onClick={handleClick}
                    style={{ cursor: "pointer" }}
                  >
                    {action}
                  </span>
                );
            }
          })}
        </Space>
      ),
    });
  }

  return (
    <div className="generic-table-container">
      <Input
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => handleSearch(e.target.value)} // Trigger debounced search
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault(); // Prevent default page reload
            onSearch(searchTerm.trim().toLowerCase()); // Trigger API manually
          }
        }}
        autoFocus
        className="search-input"
      />


      <Table
        dataSource={filteredData}
        columns={columns}
        rowKey={(record) => record.id || record.key || record.userId}
        pagination={pagination ? {
          ...pagination,
          showTotal: pagination.showTotal || ((total, range) => `${range[0]}-${range[1]} of ${total} items`),
          showSizeChanger: pagination.showSizeChanger !== undefined ? pagination.showSizeChanger : false,
          showQuickJumper: pagination.showQuickJumper !== undefined ? pagination.showQuickJumper : false,
          hideOnSinglePage: false,
          onChange: pagination?.onChange,
        } : false}
        scroll={{ x: 1000 }}
      />
    </div>
  );
};

export default GenericTable;
